/**
 * Session-scoped cache for AI report translations.
 *
 * WHY not TanStack persistence: translation blocks can reach hundreds of KB and
 * the app's persisted cache is a single localStorage key shared with campaigns
 * and leads — quota eviction there breaks boot-fast rendering for everything.
 * A module-level Map survives remounts and route changes (the failure of the
 * previous useRef) at zero quota risk. Reloads are already covered by the
 * deterministic PDF file id, so cross-session translation reuse buys nothing.
 *
 * WHY the fingerprint: the key must identify the CONTENT, not just the lead.
 * The admin can change the report template (sections/order) between two
 * generations of the same lead+language; a content-blind key would replay a
 * translation whose key set no longer matches, and `validateTranslations`
 * would throw — the renderer's catch then silently renders ENGLISH (proven in
 * review probes). Hashing the source texts makes a template/lead change a
 * guaranteed cache MISS, so the mismatch can never be served.
 */

const cache = new Map<string, Record<string, string>>();
const MAX_ENTRIES = 40;

/** Stable, cheap hash of a texts block: every key and every character counts. */
export function textsFingerprint(texts: Record<string, string>): string {
  let h = 5381;
  for (const k of Object.keys(texts).sort()) {
    const s = k + '|' + texts[k];
    for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  }
  return h.toString(36);
}

/** Cache key: lead + variant + language + exact content being translated. */
export const translationCacheKey = (
  leadId: string, variant: string, language: string, texts: Record<string, string>
): string => `${leadId}|${variant}|${language}|${textsFingerprint(texts)}`;

export function getCachedTranslation(key: string): Record<string, string> | undefined {
  return cache.get(key);
}

export function setCachedTranslation(key: string, translations: Record<string, string>): void {
  // Simple size guard: translations are per-lead, so unbounded growth across a
  // long session is otherwise possible. Oldest-inserted entries evicted first.
  if (!cache.has(key) && cache.size >= MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(key, translations);
}
